import $api from "../api/axios";

export type DeficiencySeverity = "critical" | "major" | "minor";

export type DeficiencyStatus = "open" | "in_review" | "cured" | "dismissed" | "stale";

export type DeficiencySubject = "memo_chapter" | "case_brief" | "projects_list_chat";

export type AdminDeficiency = {
  id: number;
  subject: DeficiencySubject;
  organizationId: number | null;
  projectId: number | null;
  caseBriefId: number | null;
  chapterAId: number | null;
  chapterAQuote: string;
  chapterBId: number | null;
  chapterBQuote: string | null;
  type: string;
  typeLabel: string | null;
  severity: DeficiencySeverity;
  severityOverridden: boolean;
  description: string;
  rootCause: string | null;
  fixNeeds: string | null;
  attachmentUrl: string | null;
  attachmentName: string | null;
  detectedBy: "ai" | "human";
  detectedByUserId: number | null;
  status: DeficiencyStatus;
  curedByRevisionId: number | null;
  curedByRevisionNumber: number | null;
  reviewedByUserId: number | null;
  reviewedAt: string | null;
  scanRound: number;
  firstSeenScanRound: number | null;
  lastSeenScanRound: number | null;
  createdAt: string;
  updatedAt: string;
  projectName: string | null;
  organizationName: string | null;
  projectVersion: string | null;
  caseBriefName: string | null;
  chapterAName: string | null;
  chapterBName: string | null;
  detectedByUserName: string | null;
  detectedByUserEmail: string | null;
  reviewedByUserName: string | null;
  proposedFix: string | null;
  proposedFixBy: "ai" | "human" | null;
  lastScannedAt: string | null;
};

export type DetectionRules = {
  rules: string;
  isCustom: boolean;
  updatedAt: string | null;
};

export async function getAdminDeficiencies(projectId?: number): Promise<AdminDeficiency[]> {
  const response = await $api.get<{ deficiencies: AdminDeficiency[] }>("/deficiencies/admin", {
    params: projectId ? { projectId } : {}
  });
  return response.data.deficiencies;
}

export async function getDeficiency(id: number): Promise<AdminDeficiency> {
  const response = await $api.get<AdminDeficiency>(`/deficiencies/${id}`);
  return response.data;
}

export type DeficiencyProject = {
  id: number;
  name: string;
  versionGroupId: string | null;
  versionDisplayName: string;
  organizationName: string | null;
};

export async function getDeficiencyProjects(): Promise<DeficiencyProject[]> {
  const response = await $api.get<{ projects: DeficiencyProject[] }>("/deficiencies/projects");
  return response.data.projects;
}

export async function updateDeficiency(
  id: number,
  data: { severity?: DeficiencySeverity; status?: DeficiencyStatus }
): Promise<void> {
  await $api.patch(`/deficiencies/${id}`, data);
}

export async function saveLocalFix(deficiencyId: number, proposedText: string): Promise<void> {
  await $api.put(`/deficiencies/${deficiencyId}/local-fix`, { proposedText });
}

export async function createPromptRevisionForDeficiency(
  featureKey: string,
  deficiencyId: number
): Promise<{ revisionId: number; droppedKeys: string[] }> {
  const response = await $api.post<{ revisionId: number; droppedKeys: string[] }>(
    `/admin/prompts/features/${featureKey}/revisions`,
    { deficiencyId, note: `Cure deficiency #${deficiencyId}` }
  );
  return response.data;
}

export async function getDetectionRules(): Promise<DetectionRules> {
  const response = await $api.get<DetectionRules>("/deficiencies/detection-rules");
  return response.data;
}

export async function updateDetectionRules(rules: string | null): Promise<DetectionRules> {
  const response = await $api.patch<DetectionRules>("/deficiencies/detection-rules", { rules });
  return response.data;
}

export async function rescanDeficiencies(projectId: number): Promise<{ generationId: number }> {
  const response = await $api.post<{ generationId: number }>("/deficiencies/scan", { projectId });
  return response.data;
}

export async function getGenerationTaskStatus(generationId: number): Promise<{ status: string; result?: string | null }> {
  const response = await $api.get<{ status: string; result?: string | null }>(`/generation-tasks/${generationId}/status`);
  return response.data;
}
